import cx from "classnames";
import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { canv } from "@/canv/global";
import { debounce } from "@/canv/utils";
import { default as ClearIcon } from "./ClearIcon.svg?react";
import { STICKERS } from "./constants";
import { Emojis } from "./Emojis";
import { Stickers } from "./Stickers";
import { type CachedStickerType, stickerCache } from "./stickerCache";
import style from "./stickersWindow.module.css";

enum WindowType {
    emojis = "emojis",
    stickers = "stickers",
}

export function StickersWindowLayout() {
    const [searchValue, setSearchValue] = useState("");
    const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
    const [windowTab, setWindowTab] = useState(WindowType.emojis);
    const [stickers, setStickers] = useState<CachedStickerType[]>([]);
    const searchInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (stickerCache.isCacheInitialized()) {
            setStickers(stickerCache.getLoadedStickers());
        } else {
            stickerCache.initializeStickers(STICKERS).then(() => {
                setStickers(stickerCache.getLoadedStickers());
            });
        }
    }, []);

    const onFocus = () => {
        canv.appInterface.inputFocused = 1;
    };

    const onBlur = () => {
        canv.appInterface.inputFocused = 0;
    };

    const onSearch = useCallback((value: string) => {
        if (value) {
            const foundStickers: CachedStickerType[] = [];
            const allCachedStickers = stickerCache.getLoadedStickers();

            for (let i = 0; i < allCachedStickers.length; i++) {
                const sticker = allCachedStickers[i];

                if (sticker.tags.some((tag) => tag.match(value))) {
                    foundStickers.push(sticker);
                }
            }

            setStickers(foundStickers);
        } else {
            setStickers(stickerCache.getLoadedStickers());
        }
    }, []);

    const resetSearch = useCallback(() => {
        setSearchValue("");
        setDebouncedSearchValue("");
        onSearch("");
    }, [onSearch]);

    const debouncedSearch = useMemo(
        () =>
            debounce((value: string) => {
                setDebouncedSearchValue(value);
                onSearch(value);
            }, 500),
        [onSearch],
    );

    const onClear = () => {
        resetSearch();
        searchInputRef.current?.focus();
    };

    const onChangeSearchValue = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchValue(value);
        debouncedSearch(value);
    };

    const stickersButtonClassName = cx(style.typeButton, {
        [style.activeTypeButton]: windowTab === WindowType.stickers,
    });

    const emojisButtonClassname = cx(style.typeButton, {
        [style.activeTypeButton]: windowTab === WindowType.emojis,
    });

    return (
        <div className={style.stickersWindow}>
            <div className={style.header}>
                <input
                    ref={searchInputRef}
                    className={style.search}
                    onChange={onChangeSearchValue}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    value={searchValue}
                    placeholder="Поиск"
                    type="text"
                />
                {searchValue && (
                    <button className={style.clearButton} onClick={onClear} type="button">
                        <ClearIcon />
                    </button>
                )}
            </div>
            <div className={style.typeButtons}>
                <button
                    className={emojisButtonClassname}
                    onClick={() => setWindowTab(WindowType.emojis)}
                    type="button"
                >
                    Эмодзи
                </button>
                <button
                    className={stickersButtonClassName}
                    onClick={() => setWindowTab(WindowType.stickers)}
                    type="button"
                >
                    Стикеры
                </button>
            </div>
            {windowTab === WindowType.emojis && (
                <Emojis searchQuery={debouncedSearchValue} onSearchReset={resetSearch} />
            )}
            {windowTab === WindowType.stickers && (
                <Stickers stickers={stickers} searchQuery={debouncedSearchValue} />
            )}
        </div>
    );
}
